import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { usePermission } from '../hooks/usePermission';
import {
  getInvitations,
  createInvitation,
  revokeInvitation,
  CreateInvitationData,
} from '../services/invitationService';
import { Invitation, InvitationType, Role } from '@easydone/shared';
import {
  PlusIcon,
  LinkIcon,
  QrCodeIcon,
  XMarkIcon,
  ClockIcon,
  UsersIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';
import { timestampToMillis } from '../utils/timestamp';

export function InvitationsPage() {
  const { userData } = useAuth();
  const { currentOrg } = useOrg();
  const canManageUsers = usePermission('users.manage');

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  useEffect(() => {
    loadInvitations();
  }, [currentOrg]);

  const loadInvitations = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await getInvitations(currentOrg.id);
      setInvitations(data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (data: CreateInvitationData) => {
    if (!currentOrg || !userData) return;

    try {
      await createInvitation(currentOrg.id, userData.id, data);
      setShowCreateModal(false);
      loadInvitations();
    } catch (error) {
      console.error('Failed to create invitation:', error);
      alert('Failed to create invitation');
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      await revokeInvitation(invitationId);
      loadInvitations();
    } catch (error) {
      console.error('Failed to revoke invitation:', error);
      alert('Failed to revoke invitation');
    }
  };

  const copyInvitationLink = (token: string) => {
    const url = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const getInvitationUrl = (token: string) => {
    return `${window.location.origin}/join/${token}`;
  };

  const isExpired = (invitation: Invitation) => {
    const expiresAt = timestampToMillis(invitation.expiresAt);
    return expiresAt < Date.now();
  };

  const isMaxedOut = (invitation: Invitation) => {
    return invitation.maxUses ? invitation.usedCount >= invitation.maxUses : false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading invitations...</div>
      </div>
    );
  }

  if (!canManageUsers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">You don't have permission to manage invitations.</div>
      </div>
    );
  }

  const activeInvitations = invitations.filter(i => i.isActive && !isExpired(i) && !isMaxedOut(i));
  const inactiveInvitations = invitations.filter(i => !i.isActive || isExpired(i) || isMaxedOut(i));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Invitations</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Create and manage invitation links for new team members
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center text-sm sm:text-base"
        >
          <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
          Create Invitation
        </button>
      </div>

      {/* Active Invitations */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Active Invitations ({activeInvitations.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeInvitations.map((invitation) => (
            <InvitationCard
              key={invitation.id}
              invitation={invitation}
              onRevoke={handleRevokeInvitation}
              onCopyLink={copyInvitationLink}
              onViewQR={() => setSelectedInvitation(invitation)}
              copied={copiedToken === invitation.token}
            />
          ))}
          {activeInvitations.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-500">
              No active invitations. Create one to get started.
            </div>
          )}
        </div>
      </div>

      {/* Inactive Invitations */}
      {inactiveInvitations.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Expired / Revoked ({inactiveInvitations.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inactiveInvitations.map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                onRevoke={handleRevokeInvitation}
                onCopyLink={copyInvitationLink}
                onViewQR={() => setSelectedInvitation(invitation)}
                copied={copiedToken === invitation.token}
                inactive
              />
            ))}
          </div>
        </div>
      )}

      {/* Create Invitation Modal */}
      {showCreateModal && (
        <CreateInvitationModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateInvitation}
        />
      )}

      {/* QR Code Modal */}
      {selectedInvitation && (
        <QRCodeModal
          invitation={selectedInvitation}
          url={getInvitationUrl(selectedInvitation.token)}
          onClose={() => setSelectedInvitation(null)}
        />
      )}
    </div>
  );
}

interface InvitationCardProps {
  invitation: Invitation;
  onRevoke: (id: string) => void;
  onCopyLink: (token: string) => void;
  onViewQR: () => void;
  copied: boolean;
  inactive?: boolean;
}

function InvitationCard({ invitation, onRevoke, onCopyLink, onViewQR, copied, inactive }: InvitationCardProps) {
  const formatDate = (timestamp: any) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getTypeIcon = (type: InvitationType) => {
    switch (type) {
      case 'qr-code':
        return <QrCodeIcon className="w-5 h-5" />;
      case 'single-use':
        return <UsersIcon className="w-5 h-5" />;
      case 'multi-use':
        return <UsersIcon className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type: InvitationType) => {
    switch (type) {
      case 'qr-code':
        return 'QR Code';
      case 'single-use':
        return 'Single Use';
      case 'multi-use':
        return 'Multi Use';
    }
  };

  return (
    <div className={`bg-white border rounded-lg p-4 ${inactive ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getTypeIcon(invitation.type)}
          <span className="font-medium text-gray-900">{getTypeLabel(invitation.type)}</span>
        </div>
        {!invitation.isActive && (
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Revoked</span>
        )}
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <span className="font-medium">Role:</span>
          <span className="ml-2 bg-primary-100 text-primary-800 px-2 py-0.5 rounded text-xs">
            {invitation.role.replace('_', ' ')}
          </span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <ClockIcon className="w-4 h-4 mr-1" />
          <span>Expires: {formatDate(invitation.expiresAt)}</span>
        </div>

        <div className="flex items-center text-sm text-gray-600">
          <UsersIcon className="w-4 h-4 mr-1" />
          <span>
            Used: {invitation.usedCount}
            {invitation.maxUses ? ` / ${invitation.maxUses}` : ' / ∞'}
          </span>
        </div>
      </div>

      {invitation.notes && (
        <p className="text-xs text-gray-500 mb-4 italic">{invitation.notes}</p>
      )}

      <div className="flex gap-2">
        {invitation.type === 'qr-code' && (
          <button
            onClick={onViewQR}
            className="flex-1 btn btn-secondary text-xs sm:text-sm py-1.5"
          >
            <QrCodeIcon className="w-4 h-4 mr-1" />
            View QR
          </button>
        )}
        <button
          onClick={() => onCopyLink(invitation.token)}
          className="flex-1 btn btn-secondary text-xs sm:text-sm py-1.5"
        >
          {copied ? (
            <>
              <CheckIcon className="w-4 h-4 mr-1" />
              Copied!
            </>
          ) : (
            <>
              <LinkIcon className="w-4 h-4 mr-1" />
              Copy Link
            </>
          )}
        </button>
        {invitation.isActive && (
          <button
            onClick={() => onRevoke(invitation.id)}
            className="btn btn-secondary text-xs sm:text-sm py-1.5"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

interface CreateInvitationModalProps {
  onClose: () => void;
  onCreate: (data: CreateInvitationData) => Promise<void>;
}

function CreateInvitationModal({ onClose, onCreate }: CreateInvitationModalProps) {
  const [type, setType] = useState<InvitationType>('single-use');
  const [role, setRole] = useState<Role>(Role.OPERATOR);
  const [expiryDays, setExpiryDays] = useState(7);
  const [maxUses, setMaxUses] = useState<number | undefined>(1);
  const [notes, setNotes] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiryDays);

      await onCreate({
        type,
        role,
        expiresAt,
        maxUses: type === 'qr-code' ? undefined : maxUses,
        notes,
        customMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[95vh] overflow-y-auto">
        <h2 className="text-lg sm:text-xl font-bold mb-4">Create Invitation</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invitation Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as InvitationType)}
              className="input w-full"
            >
              <option value="single-use">Single Use (one person)</option>
              <option value="multi-use">Multi Use (team)</option>
              <option value="qr-code">QR Code (unlimited, for printing)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {type === 'single-use' && 'Link can only be used once'}
              {type === 'multi-use' && 'Link can be used multiple times'}
              {type === 'qr-code' && 'For printing on posters, unlimited uses'}
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="input w-full"
            >
              <option value={Role.OPERATOR}>Operator</option>
              <option value={Role.TEAM_LEADER}>Team Leader</option>
              <option value={Role.LOCAL_ENGINEER}>Local Engineer</option>
              <option value={Role.CENTRAL_ENGINEER}>Central Engineer</option>
              <option value={Role.ORG_ADMIN}>Org Admin</option>
              <option value={Role.CLIENT_VIEWER}>Client Viewer</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires In (days)
            </label>
            <select
              value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))}
              className="input w-full"
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year</option>
            </select>
          </div>

          {type !== 'qr-code' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Uses
              </label>
              <input
                type="number"
                value={maxUses || ''}
                onChange={(e) => setMaxUses(e.target.value ? Number(e.target.value) : undefined)}
                className="input w-full"
                min="1"
                placeholder="Unlimited"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty for unlimited uses
              </p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input w-full"
              placeholder="e.g., For new operators at Site A"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Message (optional)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              className="input w-full"
              rows={3}
              placeholder="Message to show to invited users"
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary w-full sm:w-auto"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary w-full sm:w-auto"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface QRCodeModalProps {
  invitation: Invitation;
  url: string;
  onClose: () => void;
}

function QRCodeModal({ invitation, url, onClose }: QRCodeModalProps) {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold">QR Code Invitation</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="text-center mb-6">
          <div className="inline-block p-4 bg-white border-2 border-gray-200 rounded-lg">
            <QRCodeSVG value={url} size={256} level="H" />
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Scan this QR code to join as <strong>{invitation.role.replace('_', ' ')}</strong>
          </p>
          {invitation.customMessage && (
            <p className="text-sm text-gray-500 mt-2 italic">{invitation.customMessage}</p>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Invitation Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={url}
              readOnly
              className="input flex-1 text-xs sm:text-sm"
            />
            <button
              onClick={() => navigator.clipboard.writeText(url)}
              className="btn btn-secondary text-xs sm:text-sm"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button onClick={handlePrint} className="btn btn-primary w-full sm:flex-1">
            Print Poster
          </button>
          <button onClick={onClose} className="btn btn-secondary w-full sm:w-auto">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
